import type { Template, Worksheet } from '@/models/template';
import { ReportAPI } from '@/state/slices/reports';
import { invalidateAllReportPdfs } from '@/state/slices/studies';

/** What a new worksheet or report template starts from. */
export type StartingPoint = 'STANDARD' | 'BLANK';

export interface CreateTemplateArgs {
  name: string;
  startingPoint: StartingPoint;
}

/** Template builder endpoints, kept out of reports.ts for size. */
const ReportTemplateAPI = ReportAPI.injectEndpoints({
  endpoints: (builder) => ({
    createWorksheet: builder.mutation<Worksheet, CreateTemplateArgs>({
      query: (body) => ({ url: '/worksheets/', method: 'POST', body }),
      invalidatesTags: [{ type: 'Worksheets', id: 'LIST' }],
    }),
    createTemplate: builder.mutation<Template, CreateTemplateArgs>({
      query: (body) => ({ url: '/templates/', method: 'POST', body }),
      invalidatesTags: [{ type: 'Templates', id: 'LIST' }],
    }),
    updateTemplate: builder.mutation<Template, Template>({
      query: (template) => ({
        url: `/templates/${template.uuid}/`,
        method: 'PUT',
        body: template,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (_result, _error, template) => [
        { type: 'Templates', id: template.uuid },
        { type: 'Templates', id: 'LIST' },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAllReportPdfs());
        } catch {
          /* mutation failed, no-op */
        }
      },
    }),
    deleteTemplate: builder.mutation<void, string>({
      query: (uuid) => ({ url: `/templates/${uuid}/`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, uuid) => [
        { type: 'Templates', id: uuid },
        { type: 'Templates', id: 'LIST' },
      ],
    }),
    /** Renders unsaved template edits against a study. Saves nothing. */
    previewTemplate: builder.mutation<
      Blob,
      { template: Template; studyUuid: string }
    >({
      query: ({ template, studyUuid }) => ({
        url: `/templates/${template.uuid}/preview/`,
        method: 'POST',
        body: { study: studyUuid, template },
        headers: { 'Content-Type': 'application/json' },
        responseHandler: async (response) =>
          response.ok ? response.blob() : response.json(),
      }),
    }),
  }),
});

export const {
  useCreateWorksheetMutation,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  usePreviewTemplateMutation,
} = ReportTemplateAPI;
